import { createFeature, createReducer, on } from '@ngrx/store';
import { OrganizationSupplementalSystem } from '@models/organizationSupplementalSystem.model';
import { OrganizationEMR } from '@models/organizationEMR.model';
import { OrgSupplementalSystemActions } from '@store/actions/org-supplemental-system/org-supplemental-system.actions';
import { OrganizationEMRDetailsActions } from '@store/actions/organization-emr-details/organization-emr-details.actions';
import { AddOrganizationActions } from '@store/actions/add-organization/add-organization.actions';
import { SYSTEM_TYPE } from '@constants/system-type.constants';

function mapEMRToSupplementalSystem(emr: OrganizationEMR): OrganizationSupplementalSystem {
  return {
    id: parseInt(emr.id),
    organizationID: parseInt(emr.organizationID),
    systemType: SYSTEM_TYPE.EHR,
    deliveryMethod: emr.ehrClinicalDeliveryMethod,
    frequencyOfDelivery: emr.frequencyOfDataDelivery?.toLowerCase() === 'other'
      ? (emr.otherFrequencyOfDataDelivery || 'Other')
      : emr.frequencyOfDataDelivery,
    vendorName: emr.vendorName,
    productVersion: emr.productVersion,
    fileFormat: emr.fileFormat,
    externalID: emr.externalID,
    emrID: emr.emrID,
    statusID: emr.statusID,
    effectiveDate: emr.effectiveDate,
    expirationDate: emr.expirationDate,
    _legacyEhrpmDeliveryMethod: emr.ehrpmDeliveryMethod || null,
  };
}

export const orgSupplementalSystemFeatureKey = 'orgSupplementalSystem';

export interface State {
  organizationId: string;
  systems: OrganizationSupplementalSystem[];
  isSaveDisabled: boolean;
}

export const initialState: State = {
  organizationId: '',
  systems: [],
  isSaveDisabled: false,
};

export const reducer = createReducer(
  initialState,

  on(AddOrganizationActions.getOrganizationDetailsSuccess, (state, action) => {
    const legacyEmrs: OrganizationSupplementalSystem[] =
      (action.details.organizationEMRs ?? []).map(mapEMRToSupplementalSystem);
    const supplementalSystems: OrganizationSupplementalSystem[] =
      action.details.organizationSupplementalSystems ?? [];
    return {
      ...state,
      organizationId: action.details.id ?? '',
      systems: [...legacyEmrs, ...supplementalSystems],
    };
  }),

  on(OrgSupplementalSystemActions.addOrgSupplementalSystem, (state) => ({
    ...state,
    isSaveDisabled: true,
  })),

  on(OrgSupplementalSystemActions.addOrgSupplementalSystemSuccess, (state, action) => ({
    ...state,
    systems: [...state.systems, action.data],
    isSaveDisabled: false,
  })),

  on(OrgSupplementalSystemActions.addOrgSupplementalSystemFailure, (state) => ({
    ...state,
    isSaveDisabled: false,
  })),

  on(OrgSupplementalSystemActions.addOrgSupplementalSystemConflict, (state) => ({
    ...state,
    isSaveDisabled: false,
  })),

  on(OrganizationEMRDetailsActions.addOrganizationEMR, (state) => ({
    ...state,
    isSaveDisabled: true,
  })),

  on(OrganizationEMRDetailsActions.addOrganizationEMRSuccess, (state, action) => ({
    ...state,
    systems: [...state.systems, mapEMRToSupplementalSystem(action.data)],
    isSaveDisabled: false,
  })),

  on(OrganizationEMRDetailsActions.addOrganizationEMRFailure, (state) => ({
    ...state,
    isSaveDisabled: false,
  })),

  on(OrganizationEMRDetailsActions.addOrganizationEMRConflict, (state) => ({
    ...state,
    isSaveDisabled: false,
  })),

  on(OrgSupplementalSystemActions.updateOrgSupplementalSystem, (state) => ({
    ...state,
    isSaveDisabled: true,
  })),

  on(OrgSupplementalSystemActions.updateOrgSupplementalSystemSuccess, (state, action) => ({
    ...state,
    systems: state.systems.map((sys) =>
      sys.id === action.system.id ? action.system : sys
    ),
    isSaveDisabled: false,
  })),

  on(OrgSupplementalSystemActions.updateOrgSupplementalSystemFailure, (state) => ({
    ...state,
    isSaveDisabled: false,
  })),

  on(OrgSupplementalSystemActions.updateOrgSupplementalSystemConflict, (state) => ({
    ...state,
    isSaveDisabled: false,
  })),

  on(OrgSupplementalSystemActions.deleteOrgSupplementalSystemSuccess, (state, action) => ({
    ...state,
    systems: state.systems.filter((sys) => sys.id !== action.id),
  })),
);

export const orgSupplementalSystemFeature = createFeature({
  name: orgSupplementalSystemFeatureKey,
  reducer,
});
